/**
 * AvatarShaderRenderer
 *
 *	This class will render a canvas and compute which pixels to have displayed
 *	by performing its computation on the GPU.
 *
 * 	The ThreeJS ShaderMaterial takes care of preparing the computation over to the GPU.
 *
 * @param DomElement video         The video element which will be used to grab video images for each frame.
 * @param DomElement avatarWrapper Wrapper element which will be used to append the canvas onto.
 */
import * as THREE from 'three'
import Browser from '../Browser'
import TWEEN from '@tweenjs/tween.js'

var AvatarShaderRenderer = function(video, avatarWrapper) {
    this.video = video
    this.avatarWrapper = avatarWrapper

    this.buffer = document.createElement('canvas')
    this.bufferContext = this.buffer.getContext('2d')

    this.onVideoCanPlay = this.onVideoCanPlay.bind(this)
    this.update = this.update.bind(this)

    this.video.addEventListener('canplay', this.onVideoCanPlay)

    this.hasEmbed = false

    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })

    this.canvasOffset = 450

    this.canvas = this.renderer.domElement
    this.canvas.style['max-width'] = '100%'
    this.canvas.style['max-height'] = 'calc(100vh - ' + this.canvasOffset + 'px)'

    return this
}

AvatarShaderRenderer.prototype = {
    embed: function() {
        this.avatarWrapper.appendChild(this.canvas)

        this.scene = new THREE.Scene()

        var width = this.video.videoWidth,
            height = this.video.videoHeight / 2

        this.camera = new THREE.OrthographicCamera(width / -2, width / 2, height / 2, height / -2, 1, 1000)
        this.camera.position.z = 1

        this.scene.add(this.camera)

        this.videoTexture = new THREE.Texture(this.buffer)
        this.videoTexture.minFilter = THREE.LinearFilter
        this.videoTexture.magFilter = THREE.LinearFilter

        this.videoGeometry = new THREE.PlaneGeometry(width, height)

        var shader = this.getShader()

        this.material = new THREE.ShaderMaterial({
            uniforms: {
                texture: {
                    type: 't',
                    value: this.videoTexture,
                },
            },
            vertexShader: shader.vertex,
            fragmentShader: shader.fragment,

            side: THREE.DoubleSide,
            transparent: true,
        })

        this.mesh = new THREE.Mesh(this.videoGeometry, this.material)
        this.mesh.position.y = -(this.canvasOffset / 2) + 35
        this.mesh.scale.y *= 2

        this.scene.add(this.mesh)
    },

    onVideoCanPlay: function() {
        var width = this.video.videoWidth
        var height = this.video.videoHeight

        this.buffer.width = width
        this.buffer.height = height
    },

    update: function() {
        if (this.video.readyState === this.video.HAVE_ENOUGH_DATA) {
            this.bufferContext.drawImage(this.video, 0, 0)

            if (this.videoTexture) this.videoTexture.needsUpdate = true
        }

        this.renderer.render(this.scene, this.camera)

        if (this.drawing) this.animationFrameID = requestAnimationFrame(this.update)
    },

    resize: function(width, height) {
        // notify the renderer of the size change
        this.renderer.setSize(width, height)
        // update the camera
        this.camera.aspect = width / height
        this.camera.updateProjectionMatrix()

        this.canvas.style.width = null
        this.canvas.style.height = null
    },

    start: function() {
        if (this.drawing) return
        if (!this.hasEmbed) {
            this.hasEmbed = true
            this.embed()
        }

        this.drawing = true

        this.resize(this.video.videoWidth, this.video.videoHeight / 2)

        this.update()
    },

    stop: function() {
        this.drawing = false
        cancelAnimationFrame(this.animationFrameID)
    },

    destroy: function() {
        this.video.removeEventListener('canplay', this.onVideoCanPlay)

        this.videoTexture && this.videoTexture.dispose()
        this.videoGeometry && this.videoGeometry.dispose()
        this.material && this.material.dispose()

        if (this.mesh) {
            this.mesh.material.dispose()
            this.mesh.geometry.dispose()
        }

        if (this.canvas && this.canvas.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas)
            this.canvas = null
        }
    },

    getShader: function() {
        var vertex = 'varying vec2 vUv;'
        vertex += ''
        vertex += 'void main() {'
        vertex += '    vUv = uv;'
        vertex += ''
        vertex += '    gl_Position =   projectionMatrix * '
        vertex += '                    modelViewMatrix * '
        vertex += '                    vec4(position,1.0);'
        vertex += '}'

        var fragment = 'uniform sampler2D texture;'
        fragment += ''
        fragment += 'varying vec2 vUv;'
        fragment += ''
        fragment += 'void main() {'
        fragment += '	vec4 textureColor = texture2D(texture, vUv);'
        fragment += ''
        fragment += '	if( vUv.y >= 0.5 ) {'
        fragment += '		vec4 alphaColor = texture2D(texture, vec2( vUv.x , vUv.y - 0.5 ) );'
        fragment += ''
        fragment += '		if( alphaColor.a > 0.0 ) {'
        fragment += '			textureColor.a = alphaColor.b;'
        fragment += '		} else {'
        fragment += '			textureColor.a = 0.0;'
        fragment += '		}'
        fragment += '	} else {'
        fragment += '		textureColor.a = 0.0;'
        fragment += '	}'
        fragment += ''
        fragment += '	gl_FragColor = textureColor;'
        fragment += ''
        fragment += '}'

        return {
            vertex: vertex,
            fragment: fragment,
        }
    },
}

/**
 * AvatarCanvasRenderer
 *
 *	This class will render a canvas and compute which pixels to have displayed
 *	by performing its computation on the CPU.
 *
 * @param DomElement video         The video element which will be used to grab video images for each frame.
 * @param DomElement avatarWrapper Wrapper element which will be used to append the canvas onto.
 */
var AvatarCanvasRenderer = function(video, avatarWrapper) {
    this.video = video
    this.avatarWrapper = avatarWrapper

    this.update = this.update.bind(this)
    this.onVideoCanPlay = this.onVideoCanPlay.bind(this)

    this.video.addEventListener('canplay', this.onVideoCanPlay)

    this.buffer = document.createElement('canvas')
    this.bufferContext = this.buffer.getContext('2d')

    this.canvas = document.createElement('canvas')
    this.canvas.style['max-width'] = '100%'
    this.canvas.style['max-height'] = 'calc(100vh - 450px)'

    this.canvasContext = this.canvas.getContext('2d')

    this.hasEmbed = false

    return this
}

AvatarCanvasRenderer.prototype = {
    update: function() {
        if (this.canvas) {
            var width = this.video.videoWidth
            var height = this.video.videoHeight

            if (width > 0 && height > 0) {
                //BUFFER
                this.bufferContext.drawImage(this.video, 0, 0)
                var from = this.bufferContext.getImageData(0, height / 2, width, height)

                //CANVAS
                this.canvasContext.drawImage(this.buffer, 0, 0)
                var to = this.canvasContext.getImageData(0, 0, width, height / 2)

                for (var i = 3; i < from.data.length; i += 4) {
                    if (from.data[i]) {
                        //check the alpha
                        to.data[i] = from.data[i - 1]
                    }
                }

                this.canvasContext.putImageData(to, 0, 0)
            }
        }

        if (this.drawing) this.animationFrameID = requestAnimationFrame(this.update)
    },

    embed: function() {
        this.avatarWrapper.appendChild(this.canvas)
    },

    start: function() {
        if (!this.hasEmbed) {
            this.hasEmbed = true
            this.embed()
        }

        this.drawing = true
        this.update()
    },

    stop: function() {
        this.drawing = false
        cancelAnimationFrame(this.animationFrameID)
    },

    destroy: function() {
        this.stop()

        if (this.video) {
            this.video.pause()
            this.video.removeEventListener('canplay', this.onVideoCanPlay)
            this.video = null
        }

        this.audio && this.audio.pause()
        this.audio = null

        if (this.canvas && this.canvas.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas)
            this.canvas = null
        }
    },

    onVideoCanPlay: function() {
        var width = this.video.videoWidth
        var height = this.video.videoHeight

        this.canvas.width = this.buffer.width = width
        this.buffer.height = height
        this.canvas.height = height / 2
    },
}

/**
 * AvatarPlayer constructor
 *
 *	This class is responsible for instantiating either an AvatarShaderRenderer
 *	or AvatarCanvasRenderer. The AvatarShaderRenderer requires a browser which supports
 *	webgl. It also is more efficient at renderering the Avatar as the computation is done
 *	via the GPU.
 *
 * 	If webgl isn't present, the AvatarCanvasRenderer will be used as fallback. The computation
 * 	for displaying the video frame will be done via CPU.
 *
 * @param Object options an object of options passed in for constructing the correct avatar renderer.
 */
var AvatarPlayer = function(options) {
    this.onFailedToPlay = options.onFailedToPlay || false

    this.avatarWrapper = options.avatarWrapper

    this.video = document.createElement('video')
    this.video.crossOrigin = 'Anonymous'

    this.audio = document.createElement('audio')
    this.audio.crossOrigin = 'Anonymous'
    this.update = this.update.bind(this)

    this.update()

    // Edge browsers need to use canvas to get rid of avatar glitch when moving to next stops
    this.player =
        Browser.can('webgl') && !Browser.is('Edge')
            ? new AvatarShaderRenderer(this.video, this.avatarWrapper)
            : new AvatarCanvasRenderer(this.video, this.avatarWrapper)

    return this
}

AvatarPlayer.prototype = {
    update: function(time) {
        this.updateFrameID = requestAnimationFrame(this.update)
        TWEEN.update(time)
    },

    destroy: function() {
        cancelAnimationFrame(this.updateFrameID)
        this.player.destroy()
    },

    startDrawing: function() {
        this.player.start()
    },

    stopDrawing: function() {
        this.player.stop()
    },

    getAudio: function() {
        return this.audio
    },

    getVideo: function() {
        return this.video
    },

    loadAudio: function() {
        this.audio.load()
    },

    setAudioURL: function(url) {
        this.audio.src = url
    },

    setVideoURL: function(url) {
        this.video.src = url
    },

    isVideoContainURL: function(url) {
        return this.video.src.indexOf(url) >= 0
    },

    isAudioContainURL: function(url) {
        return this.audio.src.indexOf(url) >= 0
    },

    canPlayAudio: function() {
        return (
            this.audio.readyState === this.audio.HAVE_ENOUGH_DATA ||
            this.audio.readyState === this.audio.HAVE_FUTURE_DATA
        )
    },

    canPlayVideo: function() {
        return (
            this.video.readyState === this.video.HAVE_ENOUGH_DATA ||
            this.video.readyState === this.video.HAVE_FUTURE_DATA
        )
    },

    playAudio: function() {
        if (this.audio) {
            var scope = this
            var playPromise = this.audio.play()
            // In browsers that don’t yet support this functionality,
            // playPromise won’t be defined.
            if (playPromise !== undefined) {
                playPromise
                    .then(function() {})
                    .catch(function(error) {
                        scope.onFailedToPlay && scope.onFailedToPlay(scope.audio)
                    })
            }
        }
    },

    fadeAudio: function(volume, duration, cb) {
        var scope = this

        var callback = cb || {}

        var onStart = callback.onStart
        var onStop = callback.onStop
        var onUpdate = callback.onUpdate
        var onComplete = callback.onComplete

        var audioFade = new TWEEN.Tween({ volume: this.audio.volume })
            .to({ volume: volume }, duration)
            .onStart(() => onStart && onStart())
            .onStop(() => onStop && onStop())
            .onUpdate(({ volume }) => {
                onUpdate && onUpdate(volume)
                scope.setAudioVolume(volume)
            })
            .onComplete(() => onComplete && onComplete())

        audioFade.start()

        return audioFade
    },

    setAudioVolume: function(volume) {
        this.audio.volume = volume
    },

    getAudioVolume: function() {
        return this.audio.volume
    },

    playVideo: function() {
        if (this.video) {
            var scope = this
            var playPromise = this.video.play()
            // In browsers that don’t yet support this functionality,
            // playPromise won’t be defined.
            if (playPromise !== undefined) {
                playPromise
                    .then(function() {})
                    .catch(function(error) {
                        scope.onFailedToPlay && scope.onFailedToPlay(scope.video)
                    })
            }
        }
    },

    pauseAudio: function() {
        this.audio && this.audio.pause()
    },

    pauseVideo: function() {
        this.video && this.video.pause()
    },

    setWrapper: function(avatarWrapper) {
        this.player.avatarWrapper = avatarWrapper
    },

    muteVideo: function() {
        this.video.muted = true
    },

    muteAudio: function() {
        this.audio.muted = true
    },

    unmuteVideo: function() {
        this.video.muted = false
    },

    unmuteAudio: function() {
        this.audio.muted = false
    },
}

export default AvatarPlayer
